import React, { useState } from 'react';
import { SlidersHorizontal, ChevronDown, XCircle, ArrowUpDown, X } from 'lucide-react';
export interface FilterOption {
  value: string;
  label: string;
}
export interface FilterDef {
  key: string;
  label: string;
  options: FilterOption[];
  width?: string;
}
export interface SortOption {
  value: string;
  label: string;
}
export interface FilterBarProps {
  filters: FilterDef[];
  filterValues: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClearAll: () => void;
  sortOptions?: SortOption[];
  sortValue?: string;
  onSortChange?: (value: string) => void;
  actions?: React.ReactNode;
}
export function FilterBar({
  filters,
  filterValues,
  onFilterChange,
  onClearAll,
  sortOptions,
  sortValue,
  onSortChange,
  actions
}: FilterBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const activeFilterCount = Object.values(filterValues).filter(val => val !== '').length;
  return <div className="bg-[#f0f4f4] p-4 rounded-lg flex flex-col transition-all duration-300 mb-8" data-id="element-177">
      {/* Top Row: Compact Bar */}
      <div className="flex items-center gap-3 w-full flex-wrap" data-id="element-178">
        {/* Toggle Button */}
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md px-3.5 py-2 transition-colors" data-id="element-179">
          <SlidersHorizontal className="w-4 h-4" data-id="element-180" />
          Filters
          {activeFilterCount > 0 && <span className="bg-[#458b85] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1 leading-none" data-id="element-181">
              {activeFilterCount}
            </span>}
        </button>

        {/* Active Filter Pills */}
        {Object.entries(filterValues).map(([key, value]) => {
        if (!value) return null;
        const filterDef = filters.find(f => f.key === key);
        const label = filterDef?.options.find(o => o.value === value)?.label || value;
        return <div key={key} className="bg-white border border-gray-300 rounded-full px-3.5 py-1.5 text-sm text-gray-600 flex items-center gap-1.5" data-id="element-182">
              <span className="font-medium text-gray-500 mr-1" data-id="element-183">
                {filterDef?.label.replace('Filter by ', '')}:
              </span>
              {label}
              <button onClick={() => onFilterChange(key, '')} className="ml-1 text-gray-400 hover:text-gray-600 transition-colors" aria-label={`Clear ${key} filter`} data-id="element-184">
                <X className="w-3.5 h-3.5" data-id="element-185" />
              </button>
            </div>;
      })}

        {/* Clear All Button */}
        {activeFilterCount > 0 && <button onClick={onClearAll} className="text-gray-500 hover:text-gray-700 text-sm underline cursor-pointer transition-colors" data-id="element-186">
            Clear all
          </button>}

        {/* Spacer */}
        <div className="flex-1" data-id="element-187"></div>

        {/* Sort Dropdown */}
        {sortOptions && sortOptions.length > 0 && onSortChange && <div className="relative flex items-center gap-2" data-id="element-188">
            <ArrowUpDown className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-189" />
            <select value={sortValue} onChange={e => onSortChange(e.target.value)} className="appearance-none bg-white border border-gray-300 text-gray-500 text-sm rounded-md pl-9 pr-10 py-2 outline-none focus:ring-1 focus:ring-[#458b85] w-52" data-id="element-190">
              {sortOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-191">
                  {opt.label}
                </option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-192" />
          </div>}

        {/* Actions Slot */}
        {actions && <div className="flex items-center gap-2" data-id="element-193">{actions}</div>}
      </div>

      {/* Expanded Panel: Dropdowns */}
      {isExpanded && <div className="pt-4 mt-4 border-t border-gray-200/50 flex flex-wrap gap-4 animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-194">
          {filters.map(filter => <div key={filter.key} className="relative" data-id="element-195">
              <select value={filterValues[filter.key] || ''} onChange={e => onFilterChange(filter.key, e.target.value)} className={`appearance-none bg-white border border-gray-300 text-gray-500 text-sm rounded-md pl-4 pr-10 py-2.5 outline-none focus:ring-1 focus:ring-[#458b85] ${filter.width || 'w-48'}`} data-id="element-196">
                <option value="" data-id="element-197">{filter.label}</option>
                {filter.options.map(opt => <option key={opt.value} value={opt.value} data-id="element-198">
                    {opt.label}
                  </option>)}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-199" />
            </div>)}
        </div>}
    </div>;
}